import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Loader2, CheckCircle2, Mail, Users } from 'lucide-react';
import { apiCall } from './api';

// Section « Soutenir » — 100% en français (période d'essai, utilisateurs francophones).
export default function SupportModal({ open, onClose }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', amount: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // { etransfer_email }
  const [error, setError] = useState('');
  const [wallOpen, setWallOpen] = useState(false);
  const [wall, setWall] = useState(null);

  const openWall = async () => {
    setWallOpen(true);
    if (wall === null) {
      try { const r = await apiCall('/supporters/public', 'GET'); setWall(r.supporters || []); }
      catch { setWall([]); }
    }
  };

  const submit = async () => {
    if (!form.name.trim()) { setError('Merci d’indiquer ton nom.'); return; }
    setError(''); setLoading(true);
    try {
      const r = await apiCall('/support', 'POST', form);
      setDone({ etransfer_email: r.etransfer_email || 'support.josmarttech@gmail.com' });
    } catch (e) {
      setError("Une erreur est survenue. Réessaie, s’il te plaît.");
    } finally { setLoading(false); }
  };

  const close = () => { setDone(null); setWallOpen(false); setForm({ name: '', phone: '', email: '', amount: '', message: '' }); setError(''); onClose(); };

  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={(e) => { if (e.target === e.currentTarget) close(); }} data-testid="support-modal">
        <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          className="bg-slate-900 border border-rose-400/20 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[94vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-rose-500/15 to-pink-500/5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-rose-500/20 border border-rose-400/30 flex items-center justify-center">
                <Heart size={16} className="text-rose-300" />
              </div>
              <div>
                <div className="text-rose-300/80 text-[10px] uppercase tracking-wider font-semibold">JoSmartTech Academy</div>
                <div className="text-white font-bold text-base">Soutenir l’application</div>
              </div>
            </div>
            <button onClick={close} className="text-white/40 hover:text-white p-2 rounded-lg hover:bg-white/5" data-testid="support-close"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {wallOpen ? (
              <div data-testid="support-wall">
                <button onClick={() => setWallOpen(false)} className="text-cyan-300 text-sm mb-3 hover:underline">← Retour</button>
                <div className="flex items-center gap-2 text-white font-bold mb-1"><Users size={18} className="text-rose-300" />Ils nous ont déjà soutenus</div>
                {wall === null ? (
                  <div className="text-white/50 text-sm py-6 text-center"><Loader2 size={18} className="animate-spin inline" /> Chargement...</div>
                ) : wall.length === 0 ? (
                  <p className="text-white/60 text-sm py-4">Sois la première personne à nous soutenir 💚</p>
                ) : (
                  <>
                    <p className="text-white/60 text-xs mb-3">{wall.length} personne(s) nous ont déjà soutenus. Merci à elles ! 🙏</p>
                    <div className="flex flex-wrap gap-2">
                      {wall.map((s, i) => (
                        <span key={i} className="px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-400/20 text-white/85 text-sm" data-testid={`wall-name-${i}`}>
                          {s.name}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : !done ? (
              <>
                <div className="rounded-xl border border-rose-400/20 bg-rose-500/[0.06] p-4 text-sm text-white/85 leading-relaxed">
                  <p className="font-semibold text-white mb-1">Merci d’être là 🙏</p>
                  <p>
                    Cette application est <strong>gratuite pendant la période d’essai</strong>. Si elle t’aide à progresser en anglais,
                    tu peux nous soutenir avec le montant de ton choix. Ton soutien nous motive à
                    <strong> créer plus de leçons et à améliorer l’application selon vos besoins</strong>.
                  </p>
                </div>

                <div className="space-y-3">
                  <Field label="Ton nom *" value={form.name} onChange={v => setForm({ ...form, name: v })} placeholder="Ex : Marie K." testid="support-name" />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Téléphone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} placeholder="+1 ..." testid="support-phone" />
                    <Field label="Adresse e-mail" value={form.email} onChange={v => setForm({ ...form, email: v })} placeholder="ton@email.com" testid="support-email" />
                  </div>
                  <Field label="Montant que tu souhaites envoyer" value={form.amount} onChange={v => setForm({ ...form, amount: v })} placeholder="Ex : 10 $, 20 $, 50 $..." testid="support-amount" />
                  <div>
                    <label className="text-xs text-white/60">Un petit mot (optionnel)</label>
                    <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={2}
                      className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg p-2.5 text-sm text-white placeholder-white/30 focus:border-rose-400/50 outline-none"
                      placeholder="Ex : Merci pour cette application !" data-testid="support-message" />
                  </div>
                </div>

                {error && <div className="text-rose-300 text-sm">{error}</div>}

                <button onClick={submit} disabled={loading} data-testid="support-submit"
                  className="w-full bg-rose-500 hover:bg-rose-400 disabled:opacity-60 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Heart size={16} />}
                  {loading ? 'Envoi...' : 'Soutenir maintenant'}
                </button>
                <p className="text-center text-white/40 text-[11px]">Aucun paiement n’est prélevé ici. On t’indique où envoyer ton soutien.</p>
                <button onClick={openWall} data-testid="open-wall"
                  className="w-full flex items-center justify-center gap-2 text-rose-300 hover:text-rose-200 text-sm font-semibold py-1">
                  <Users size={15} />Voir ceux qui nous ont déjà soutenus
                </button>
              </>
            ) : (
              <div className="text-center py-4 space-y-4" data-testid="support-success">
                <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center mx-auto">
                  <CheckCircle2 size={28} className="text-emerald-300" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-lg">Merci {form.name.split(' ')[0]} ! 🎉</h4>
                  <p className="text-white/70 text-sm mt-1">Ton nom a bien été enregistré.</p>
                </div>
                <div className="rounded-xl border border-emerald-400/25 bg-emerald-500/[0.08] p-4 text-left">
                  <div className="text-emerald-200/70 text-[10px] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5"><Mail size={12} />Envoie ton soutien par Interac e-Transfer</div>
                  <p className="text-white/85 text-sm">
                    Envoie le montant de ton choix à cette adresse courriel :
                  </p>
                  <div className="mt-2 bg-slate-800 border border-white/10 rounded-lg p-2.5 text-emerald-300 font-mono text-sm break-all select-all" data-testid="support-etransfer">
                    {done.etransfer_email}
                  </div>
                  <p className="text-white/60 text-xs mt-2">
                    Indique le même nom (<strong className="text-white/80">{form.name}</strong>) dans le message e-Transfer pour qu’on te reconnaisse. Merci infiniment 🙏
                  </p>
                </div>
                <div className="rounded-xl border border-rose-400/20 bg-rose-500/[0.06] p-4 text-left text-sm text-white/85 leading-relaxed">
                  <p className="font-semibold text-white mb-1">Ton aide compte énormément ❤️</p>
                  <p>
                    On apprécie vraiment ton soutien — il nous motive à créer plus de leçons et à améliorer l’application pour toi.
                    Continue à apprendre et à pratiquer chaque jour, un petit pas à la fois !
                  </p>
                  <p className="mt-2 text-white/60 text-xs">
                    ℹ️ L’application est en <strong className="text-white/80">essai gratuit</strong> pour l’instant ; elle pourra devenir payante plus tard. Ton soutien aujourd’hui nous aide à la faire grandir.
                  </p>
                </div>
                <button onClick={close} className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-2.5 rounded-xl" data-testid="support-done">Fermer</button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs text-white/60">{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg p-2.5 text-sm text-white placeholder-white/30 focus:border-rose-400/50 outline-none"
        data-testid={testid} />
    </div>
  );
}
